"use client";

import { useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import Link from "next/link";
import { ArrowRight } from "lucide-react";
import type { BlogPost } from "@/Types/responses/blog";
import { getExcerptText } from "@/utils/excerpt";
import { GRID_CLASSES } from "@/libs/grid/grid";

import "swiper/css";

const AUTOPLAY_DELAY = 7000; // ms per slide

export default function FeaturedBlogSliderClient({ posts }: { posts: BlogPost[] }) {
  const swiperRef = useRef<SwiperType | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [progress, setProgress] = useState(0);

  const goToSlide = (index: number) => swiperRef.current?.slideToLoop(index);

  return (
   <section className="relative h-screen overflow-hidden -mx-6 md:-mx-20 3xl:-mx-60">
      <Swiper
        modules={[Autoplay]}
        loop
        autoplay={{ delay: AUTOPLAY_DELAY, disableOnInteraction: false }}
        onSwiper={(swiper) => (swiperRef.current = swiper)}
        onSlideChange={(swiper) => {
          setActiveIndex(swiper.realIndex);
          setProgress(0);
        }}
        onAutoplayTimeLeft={(_swiper, _timeLeft, timeLeftRatio) => {
          // timeLeftRatio runs 1 -> 0 over the slide's dwell time
          setProgress(1 - timeLeftRatio);
        }}
        className="h-full w-full"
      >
        {posts.map((post) => (
          <SwiperSlide key={post._id}>
            <div
              className="relative h-full w-full bg-cover bg-center flex items-center"
              style={{ backgroundImage: `url(${post.featuredImage})` }}
            >
              <div className="absolute inset-0 bg-black/60" />

              <div className={`relative z-10 w-full ${GRID_CLASSES}`}>
                <div className="col-span-5 lg:col-span-8 space-y-8 text-white">
                  {post.postCategories[0] && (
                    <p className="uppercase text-sm font-semibold tracking-wide text-white/90">
                      {post.postCategories[0].categoryName}
                    </p>
                  )}

                  <h2 className="text-4xl max-w-[18ch] lg:text-5xl font-semibold leading-[110%]">{post.postTitle}</h2>

                  {post.postExcerpt && (
                    <p className="lg:text-xl lg:max-w-xl text-white/80 line-clamp-3 hidden lg:block">
                      {getExcerptText(post.postExcerpt)}
                    </p>
                  )}

                  <Link
                    href={`/blog/${post.postSlug}`}
                    className="inline-flex items-center gap-2 bg-yellow-500 hover:bg-yellow-500 transition-colors text-white font-semibold px-6 py-3 rounded-full"
                  >
                    Read Article
                    <ArrowRight size={18} />
                  </Link>
                </div>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Right-side title list + progress bars */}
<div className="absolute right-6 md:right-20 3xl:right-60 top-0 z-20 hidden lg:flex flex-col justify-center h-full w-95 gap-8">
  {posts.slice(0, 3).map((post, index) => {
    const isActive = index === activeIndex;
    return (
      <button key={post._id} onClick={() => goToSlide(index)} className="text-left group">
        <p
          className={`text-sm font-bold uppercase tracking-wide mb-2 transition-colors ${
            isActive ? "text-white" : "text-white/60 group-hover:text-white/80"
          }`}
        >
          {post.postTitle}
        </p>
        <div className="h-0.5 w-full bg-white/20 overflow-hidden rounded-full">
          <div
            className="h-full bg-orange-400 rounded-full"
            style={{
              width: isActive ? `${progress * 100}%` : "0%",
              transition: isActive ? "none" : "width 0.3s ease",
            }}
          />
        </div>
      </button>
    );
  })}
</div>
    </section>
  );
}